import firebase from "firebase/app";

import { Card, Divider, Header } from "semantic-ui-react";

import React from "react";
import { useTranslation } from "react-i18next";

import { DistributionChannel } from "astrid-firestore/src/api/distribution/types/DistributionChannel";
import updateDistributionChannel from "astrid-firestore/src/api/distribution/updateDistributionChannel";

import ArticleTypeSelect from "../../../../articles/components/ArticleTypeSelect/ArticleTypeSelect";
import useArticleChannelMetadata from "../../../../articles/hooks/useArticleChannelMetadata";
import useArticleMetadata from "../../../../articles/hooks/useArticleMetadata";
import { useProfile } from "../../../../authentication/state/profile";
import Form from "../../../../forms/components/Form/Form";
import useSimpleForm from "../../../../forms/hooks/useSimpleForm";
import PrimaryButton from "../../../../ui/components/Buttons/PrimaryButton";
import ErrorMessage from "../../../../ui/components/Messages/ErrorMessage";

import SuperAdminChannelInfo from "./SuperAdminChannelInfo";

export default function SuperAdminDistributionChannelForm({ channel }) {
	const { t } = useTranslation();

	const { form, error, onSubmit, formState } = useSimpleForm({
		schema: DistributionChannel,
		defaultValues: {
			ref: channel.ref,
			name: channel.name || "",
			abbreviation: channel.abbreviation || "",
			active: channel.active || false,
			disableArtifactDeliveryWithoutMetadata: channel.disableArtifactDeliveryWithoutMetadata || false,
			disablePrices: channel.disablePrices || false,
			forceDeliveryOfMetadataAndFilesSimultaneously:
				channel.forceDeliveryOfMetadataAndFilesSimultaneously || false,
			preventTakedown: channel.preventTakedown || false,
			acceptedLanguages: channel.acceptedLanguages || [],
			acceptedArticleTypes: channel.acceptedArticleTypes || [],
			acceptedDeliveryTypes: channel.acceptedDeliveryTypes || [],
			distributionPipelineId: channel.distributionPipelineId || null,
			maxConcurrentDistributions: channel.maxConcurrentDistributions,
			maxFileDurationInMinutes: channel.maxFileDurationInMinutes || null,
			supportsAnnouncementDate: channel.supportsAnnouncementDate || false,
			supportsTakedownDate: channel.supportsTakedownDate || false,
			releaseDateGracePeriod: channel.releaseDateGracePeriod || 0,
			takedownDateGracePeriod: channel.takeDownDateGracePeriod || 0,
			requiredMetadataKeys: channel.requiredMetadataKeys || [],
			updatedBy: useProfile(),
		},
		onSubmit: (data) => {
			return updateDistributionChannel(firebase, data);
		},
	});

	const requiredMetadataKeysOptions = [...useArticleMetadata({}), ...useArticleChannelMetadata()].map(
		({ name, label }) => ({
			key: name,
			value: name,
			text: label,
		}),
	);

	return (
		<>
			<SuperAdminChannelInfo channel={channel} />

			<Card fluid>
				<Card.Content>
					<Form form={form}>
						<Form.Group widths="equal">
							<Form.Input name="name" label={t("name", "Name")} />

							<Form.Input name="abbreviation" label={t("abbreviation", "Abbreviation")} />

							<Form.Select
								name="distributionPipelineId"
								label={t("distributionPipeline", "Distribution pipeline")}
								options={Object.values(channel.pipelines || {})?.map((pipeline) => ({
									key: pipeline.id,
									value: pipeline.id,
									text: pipeline.name,
								}))}
								allowEmpty
							/>
						</Form.Group>

						<Form.Group widths="equal">
							<Form.Select
								name="acceptedDeliveryTypes"
								label={t("acceptedDeliveryTypes", "Accepted delivery types")}
								placeholder={t("allDeliveryTypes", "All delivery types")}
								options={[
									{ key: "artifact", value: "artifact", text: t("artifact", "Artifact") },
									{ key: "metadata", value: "metadata", text: t("metadata", "Metadata") },
									{ key: "cover", value: "cover", text: t("cover", "Cover") },
								]}
								multiple
							/>

							<Form.Field
								name="acceptedArticleTypes"
								label={t("acceptedArticles", "Accepted articles")}
								placeholder={t("allArticles", "All articles")}
								multiple
							>
								<ArticleTypeSelect />
							</Form.Field>
						</Form.Group>

						<Form.Group widths="equal">
							<Form.LanguageSelect
								name="acceptedLanguages"
								label={t("acceptedLanguages", "Accepted languages")}
								placeholder={t("allLanguages", "All languages")}
								multiple
							/>
							<Form.NumberInput
								name="maxConcurrentDistributions"
								label={t("maxConcurrentDistributions", "Max concurrent distributions")}
							/>
							<Form.NumberInput
								name="maxFileDurationInMinutes"
								placeholder={t("unlimited", "Unlimited")}
								label={t("maxFileDurationInMinutes", "Max file duration in minutes")}
							/>
						</Form.Group>

						<Form.Group widths="equal">
							<Form.NumberInput
								name="releaseDateGracePeriod"
								placeholder="0"
								label={t("releaseDateGracePeriod", "Release date grace period (in days)")}
							/>
							<Form.NumberInput
								name="takedownDateGracePeriod"
								placeholder="0"
								label={t("takedownDateGracePeriod", "Takedown date grace period (in days)")}
							/>
						</Form.Group>

						<Header as="h4" content={t("metadata", "Metadata")} />

						<Form.Select
							name="requiredMetadataKeys"
							label={t("requiredMetadata", "Required metadata")}
							options={requiredMetadataKeysOptions}
							multiple
						/>

						<Form.Checkbox
							slider
							name="disablePrices"
							label={t("disablePricesForDistributionChannel", "Prevent delivery of prices in metadata")}
							labelFalse={t(
								"enablePricesForDistributionChannel",
								"Prices will be included in metadata when available",
							)}
						/>

						<Divider />

						<Header as="h4" content={t("other", "Other")} />

						<Form.Checkbox
							slider
							name="disableArtifactDeliveryWithoutMetadata"
							label={t(
								"disableArtifactDeliveryWithoutMetadata",
								"Prevent delivery of artifact without metadata",
							)}
							labelFalse={t(
								"enableArtifactDeliveryWithoutMetadata",
								"Artifact can be delivered without metadata",
							)}
						/>

						<Form.Checkbox
							slider
							name="forceDeliveryOfMetadataAndFilesSimultaneously"
							label={t(
								"forceDeliveryOfMetadataAndFilesSimultaneously",
								"Force delivery of metadata and files simultaneously",
							)}
							labelFalse={t(
								"enableForceDeliveryOfMetadataAndFilesSimultaneously",
								"Not forced to deliver metadata and files simultaneously",
							)}
						/>

						<Form.Checkbox
							slider
							name="preventTakedown"
							label={t("preventTakedown", "Takedowns are disabled")}
							labelFalse={t("preventTakedownFalse", "Takedowns are enabled")}
						/>

						<Form.Checkbox
							slider
							name="supportsAnnouncementDate"
							label={t("supportsAnnouncementDate", "Supports announcement date")}
							labelFalse={t("doesNotSupportAnnouncementDate", "Does not support announcement date")}
						/>

						<Form.Checkbox
							slider
							name="supportsTakedownDate"
							label={t("supportsTakedownDate", "Supports takedown date")}
							labelFalse={t("doesNotSupportTakedownDate", "Does not support takedown date")}
						/>

						<Form.Checkbox
							slider
							name="active"
							label={t("active", "Active")}
							labelFalse={t("inactive", "Inactive")}
						/>

						{error && <ErrorMessage error={error} />}
					</Form>
				</Card.Content>

				<Card.Content>
					<PrimaryButton
						disabled={formState.isSubmitting || !formState.isDirty}
						loading={formState.isSubmitting}
						onClick={onSubmit}
					>
						{t("save", "Save")}
					</PrimaryButton>
				</Card.Content>
			</Card>
		</>
	);
}
